<template>
  <div>
    <div class="lg:flex">
      <div class="flex flex-1 shadow items-center">
        <place-selector :styled="false" :italianOnly="true" placeholder="Cerca località" :country="['ch', 'it']" v-model="searchedPlace" class="flex-1 p-3" />
        <button class="p-3" v-if="searchedPlace" @click="searchedPlace = null">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 15 15">
            <path d="M0,0 L15,15 M15,0 L0,15" stroke="#e3064a" title="Annulla" stroke-linecap="butt"/>
          </svg>
        </button>
      </div>
      <dropdown ref="radiusDropdown" :outerlabel="`${radius}km`" innerlabel="Raggio" class="flex-1" :enabled="!!searchedPlace">
        <a
          class="border-t border-line p-3 block"
          v-for="r of [0, 5, 10, 25, 50, 100]"
          @click="radius = r"
          :key="`r-${r}`"
        >{{r}}km</a>
      </dropdown>
    </div>
    <div class="p-3 py-12">
      <template v-if="places.length">
        <div
          v-for="place in places"
          :key="place.grid"
          class="my-4"
        >
          <div>
            {{place.place.name}} <template v-if="radius">+ {{radius}}km</template> ({{place.total}})
          </div>
          <div class="flex space-x-1">
            <div 
              v-for="(count, variant) in place.variants"
              :key="variant"
              :style="{width: `${100*count/place.total}%`, backgroundColor: variantColors[variant]}"
              :title="`${variant}: ${Math.round(100*count/place.total)}%`"
              class="h-2 flex-shrink">
            </div>
          </div>
          
        </div>
      </template>
      <template v-else>
        <div class="my-4">
          <div class="text-center">
            Non ci sono ancora suffiienti dati da visualizzare<br />
            <template v-if="searchedPlace">
              Prova ad aumentare il raggio di ricerca
            </template>
          </div>
        </div>
      </template>
    </div>
    
    <card title="Legenda" type="square" class="rounded-b-lg">
      <div class="flex p-3 flex-wrap gap-8">
        <div
          v-for="(color, variant) in variantColors"
          :key="variant"
          class="flex flex-1"
        >
          <div class="h-6 w-6 mr-4" :style="{backgroundColor: color}"></div>
          <div class="whitespace-no-wrap">{{variant}}</div>
        </div>
      </div>
    </card>
  </div>
</template>

<script>
import { gridToCoords, loadWordData } from '@/services/mapdata'
import { mapGetters } from 'vuex'
import PlaceSelector from '../PlaceSelector.vue'
import { reverseLookup } from '@/services/places'

function deg2rad (deg) {
  return deg * (Math.PI / 180)
}
function distanceInKm({lat: lat1, lon: lon1}, {lat: lat2, lon: lon2}) {
  const R = 6371 // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1)
  const dLon = deg2rad(lon2 - lon1)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  const d = R * c // Distance in km
  return d
}

export default {
  components: { PlaceSelector },
  props: {
    word: {
      type: Object,
      required: true
    },
    colors: {
      type: Array,
      required: true
    },
  },
  data () {
    return {
      searchedPlace: null,
      radius: 0,
    }
  },
  watch: {
    radius: function () {
      this.$refs.radiusDropdown.close();
    }
  },
  computed: {
    ...mapGetters("wordmaps", ["mapUrls"]),
    ...mapGetters("surveys", ["words"]),

    searchedCoords() {
      if (this.searchedPlace) {
        return gridToCoords(this.searchedPlace.grid)
      }
      return undefined
    },

    variants() {
      return [this.word.word, ...this.word.variants]
    },

    variantColors() {
      return this.variants.reduce((acc, variant) => {
        acc[variant] = this.colors[this.variants.indexOf(variant)]
        return acc
      }, {})
    },
  },
  asyncComputed: {
    places: { 
      async get() {
      
        const globalCounter = new Object(null)
        const variantCountForPlace = new Object(null)
        
        for(const v of this.variants) {
          const url = this.mapUrls[v]
          if(url) {
            const data = await loadWordData(url)
            data.counter.forEach((count, place) => {
              
              if(place in globalCounter) {
                globalCounter[place] = globalCounter[place] + count
              } else {
                globalCounter[place] = count
              }

              if(!variantCountForPlace[place]) {
                variantCountForPlace[place] = new Object(null)
              }
              if(!variantCountForPlace[place][v]) {
                variantCountForPlace[place][v] = count
              } else {
                variantCountForPlace[place][v] += count
              }
            })
          }
        }


        if(this.searchedPlace) {
          const place = {
            place: this.searchedPlace,
            total: 0,
            variants: new Object(null)
          };
          if(this.radius) {
            //filter places by radius in km

            for(const [grid, variants] of Object.entries(variantCountForPlace)) {
              const placeCoords = gridToCoords(grid)
              const distance = distanceInKm(this.searchedCoords, placeCoords)
              if(distance <= this.radius) {
                place.total += globalCounter[grid]
                for(const [variant, count] of Object.entries(variants)) {
                  if(!place.variants[variant]) {
                    place.variants[variant] = count
                  } else {
                    place.variants[variant] += count
                  }
                }
              }
            }
          } else {
            place.total = globalCounter[this.searchedPlace.grid] || 0
            place.variants = variantCountForPlace[this.searchedPlace.grid] || new Object(null)
          }

          if (place.total) {
            place.variants = Object.fromEntries(
              Object.entries(place.variants).sort(([a], [b]) => 
                this.variants.indexOf(a) - this.variants.indexOf(b)
              )
            )
            return [place]
          } else {
            return []
          }
          
        } else {
          //sort globalCounter by value
          const globalCounterSorted = Array.from(Object.entries(globalCounter)).sort((a, b) => b[1] - a[1])

          return Promise.all(globalCounterSorted.slice(0, 5).map(e => {
            return new Promise(resolve => {
              reverseLookup(e[0]).then(place => {
                resolve({
                  place,
                  total: e[1],
                  variants: variantCountForPlace[e[0]]
                })
              })
            })
          }))
        }
      },
      watch: ['variants', 'searchedPlace', 'radius'],
      default: []
    }
  }
}
</script>

<style>

</style>